import type { AttendanceCorrectionRequestFilters } from '../types/attendanceCorrection';
import {
  parseCalendarDateInput,
  toCalendarDateString,
  todayBusinessCalendarDate,
} from './dateTime';
import { isValidLeaveDate, validateLeaveDateRange } from './leaveFilters';

export function createDefaultAttendanceCorrectionDateRange(now = new Date()): {
  fromDate: string;
  toDate: string;
} {
  const year = now.getFullYear();
  return {
    fromDate: `${year}-01-01`,
    toDate: `${year}-12-31`,
  };
}

export function createDefaultAttendanceCorrectionFilters(
  now = new Date(),
): AttendanceCorrectionRequestFilters {
  return createDefaultAttendanceCorrectionDateRange(now);
}

export function yesterdayBusinessCalendarDate(now = new Date()): string {
  const today = todayBusinessCalendarDate(now);
  const parsed = parseCalendarDateInput(today);
  if (!parsed) return today;
  parsed.setDate(parsed.getDate() - 1);
  return toCalendarDateString(parsed);
}

/** Today and earlier dates can be corrected. Future dates cannot. */
export function isAllowedAttendanceCorrectionDate(
  value: string,
  now = new Date(),
): boolean {
  if (!isValidLeaveDate(value)) return false;
  return value <= todayBusinessCalendarDate(now);
}

export function isPastAttendanceCorrectionDate(
  value: string,
  now = new Date(),
): boolean {
  return isAllowedAttendanceCorrectionDate(value, now);
}

export function validateAttendanceCorrectionDateRange(
  fromDate: string,
  toDate: string,
): string | null {
  return validateLeaveDateRange(fromDate, toDate);
}

export function normalizeAttendanceCorrectionFilters(input: {
  fromDate?: string;
  toDate?: string;
  page?: number;
  pageSize?: number;
}): {
  filters: AttendanceCorrectionRequestFilters;
  page: number;
  pageSize: number;
} {
  const defaults = createDefaultAttendanceCorrectionDateRange();
  const fromDate = input.fromDate?.trim() || defaults.fromDate;
  const toDate = input.toDate?.trim() || defaults.toDate;
  const rangeError = validateAttendanceCorrectionDateRange(fromDate, toDate);
  if (rangeError) {
    throw Object.assign(new Error(rangeError), { code: 'INVALID_DATE_RANGE' });
  }

  const page = input.page ?? 1;
  const pageSize = input.pageSize ?? 20;
  if (!Number.isInteger(page) || page <= 0) {
    throw Object.assign(new Error('page must be a positive integer'), {
      code: 'VALIDATION_ERROR',
    });
  }
  if (!Number.isInteger(pageSize) || pageSize <= 0 || pageSize > 100) {
    throw Object.assign(new Error('pageSize must be between 1 and 100'), {
      code: 'VALIDATION_ERROR',
    });
  }

  return {
    filters: { fromDate, toDate },
    page,
    pageSize,
  };
}
